const SPEAKING_ATTR = 'data-kia-speaking';

export interface PlayButtonHandlers {
  /** Returns the current final response text, or null if none is available yet. */
  getText: () => string | null;
  /** Returns the BCP-47 language tag for getText(), or null if none is available yet. */
  getLanguage: () => string | null;
  onPlay: (text: string, language: string | null) => void;
  onStop: () => void;
}

/**
 * Wires the configured play element to AudioOutput without letting it
 * originate or alter any text itself — it only ever plays back the stored
 * final response text supplied by `getText()`, per the contract:
 * final Gemini response text -> AudioOutput.speak(finalText).
 */
export class PlayButtonController {
  private speaking = false;

  constructor(
    private readonly element: HTMLElement,
    private readonly handlers: PlayButtonHandlers,
    private readonly signal: AbortSignal,
  ) {
    this.element.setAttribute('aria-pressed', 'false');
    this.element.setAttribute(SPEAKING_ATTR, 'false');
    this.element.addEventListener('click', this.handleClick, { signal: this.signal });
  }

  private handleClick = (): void => {
    if (this.speaking) {
      this.setSpeaking(false);
      this.handlers.onStop();
      return;
    }

    const text = this.handlers.getText();
    if (!text) {
      return;
    }
    this.setSpeaking(true);
    this.handlers.onPlay(text, this.handlers.getLanguage());
  };

  private setSpeaking(value: boolean): void {
    this.speaking = value;
    this.element.setAttribute(SPEAKING_ATTR, String(value));
    this.element.setAttribute('aria-pressed', String(value));
  }

  /**
   * Called by the SDK whenever playback starts other than through this
   * button's own click (e.g. auto-play once a response is ready), so a
   * later click on this same button still correctly reads as "stop" instead
   * of trying to start a second, redundant playback.
   */
  notifyPlaybackStarted(): void {
    this.setSpeaking(true);
  }

  /** Called by the SDK once playback finishes so the button resets. */
  notifyPlaybackEnded(): void {
    this.setSpeaking(false);
  }

  destroy(): void {
    this.speaking = false;
    this.element.removeAttribute(SPEAKING_ATTR);
    this.element.removeAttribute('aria-pressed');
  }
}
